import type { Trainer } from "../types/PersologOnlineTypes";

export type TrainerLookup = Record<string, Trainer>;

/**
 * Display name for a trainer id resolved from the lookup: "First Last" when a last name is known,
 * otherwise the email, otherwise the raw id (when the trainer is not in the lookup).
 */
export function getTrainerName(trainerLookup: TrainerLookup, trainer: string): string {
    const entry = trainerLookup[trainer];
    if (entry) {
        if (entry.lastname && entry.lastname !== "") {
            return entry.firstname + " " + entry.lastname;
        }
        return entry.email;
    }
    return trainer;
}

/**
 * Display name with the email in parentheses, e.g. "May Muster (may.muster@test.com)". Falls back
 * to the plain name when no email is known or the name already is the email.
 */
export function getTrainerWithEmail(trainerLookup: TrainerLookup, trainer: string): string {
    const name = getTrainerName(trainerLookup, trainer);
    const email = trainerLookup[trainer]?.email;
    if (email && name !== email) {
        return `${name} (${email})`;
    }
    return name;
}

/** The purchases view modes (2, 3) that include sub-account purchases. */
export function isSubAccountViewMode(viewModeValue: number | undefined): boolean {
    return viewModeValue === 2 || viewModeValue === 3;
}

/**
 * Unfiltered total for the distributor purchases table. Sub-account purchases stream in page by
 * page, so the displayed rows alone would undercount while paging; add the quantity of the in-range
 * sub-account purchases not loaded yet so the label already shows the eventual total.
 */
export function computePurchasesTableTotalQuantity(
    displayedQuantity: number,
    viewModeValue: number | undefined,
    subAccountTotalQuantity: number,
    loadedSubAccountQuantity: number,
): number {
    const unloadedSubAccountQuantity = isSubAccountViewMode(viewModeValue)
        ? subAccountTotalQuantity - loadedSubAccountQuantity
        : 0;
    return displayedQuantity + unloadedSubAccountQuantity;
}

/**
 * Whether the table has scrolled close enough to the end of the loaded rows to warrant prefetching
 * the next sub-account page.
 */
export function shouldPrefetchNextSubAccountPage(first: number, rows: number, loadedCount: number): boolean {
    const lastVisibleRowIndex = first + rows;
    return lastVisibleRowIndex >= loadedCount - rows;
}

/** The unique, truthy ids from `ids` that are not yet present in the lookup. */
export function missingTrainerIds(existing: TrainerLookup, ids: string[]): string[] {
    const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
    return uniqueIds.filter((id) => !existing[id]);
}

/** Merges resolved trainers into the lookup, returning the existing lookup when nothing resolved. */
export function mergeTrainerLookup(existing: TrainerLookup, resolved: Trainer[]): TrainerLookup {
    if (resolved.length === 0) {
        return existing;
    }
    return {
        ...existing,
        ...Object.fromEntries(resolved.map((trainer) => [trainer.id, trainer] as const)),
    };
}
